import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Trash2, FileText, Scale, AlertTriangle } from 'lucide-react';
import { Alert, Button, Form } from 'react-bootstrap';
import axios from '../../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import VatVerificationViewModal from '../VatVerificationViewModal';
import { resolveRoleAccounts } from '../../../../../services/api/chartRolesApi';

const RecordStep = ({
    recordVerification,
    handleDeleteVerification,
    handleCreateRecordVerification,
    vatPeriod,
    operationInProgress,
    isPreviewMode
}) => {
    const { companyId } = useParams();
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [recordDate, setRecordDate] = useState(vatPeriod?.due_date || '');
    const [reconciliation, setReconciliation] = useState(null);
    // The accounts are resolved, not written into the copy. An enskild firma's
    // skattekonto is 2012, not 1630 (R-FORM-6 / G37), so prose that names 1630
    // is wrong for exactly the tenants least likely to catch it.
    const [roleAccounts, setRoleAccounts] = useState({});
    const isCompleted = !!recordVerification;

    useEffect(() => {
        let cancelled = false;
        resolveRoleAccounts(companyId, ['vat_settlement', 'tax_account'])
            .then((accounts) => { if (!cancelled) setRoleAccounts(accounts || {}); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId]);

    const accountLabel = (role, fallback) => {
        const account = roleAccounts?.[role];
        return account?.accountNumber ? account.accountNumber : fallback;
    };

    // G23 — the skattekonto reconciliation endpoints existed and had no
    // consumer, so the one screen where "did the right amount actually move?"
    // is the question showed nothing. Read-only, best-effort: a tenant that
    // never imports skattekontoutdrag simply sees nothing here.
    const loadReconciliation = useCallback(async () => {
        const periodId = vatPeriod?.vat_reporting_period_id;
        if (!periodId) return;
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/vat-periods/${periodId}/reconciliation`),
                { withCredentials: true }
            );
            setReconciliation(response.data?.data || response.data || null);
        } catch (error) {
            setReconciliation(null);
        }
    }, [companyId, vatPeriod?.vat_reporting_period_id]);

    useEffect(() => { loadReconciliation(); }, [loadReconciliation, recordVerification]);

    const status = reconciliation?.reconciliation;
    const hasStatements = Number(status?.matchedStatements || 0) > 0;

    return (
        <div className="step-container">
            <div className="step-content">
                {status && hasStatements && (
                    <Alert
                        variant={status.isReconciled ? 'light' : 'warning'}
                        className="d-flex align-items-start"
                    >
                        {status.isReconciled
                            ? <Scale size={18} className="me-2 mt-1 flex-shrink-0" />
                            : <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />}
                        <div>
                            <div className="fw-semibold">
                                {status.isReconciled
                                    ? 'Stämmer mot skattekontot'
                                    : 'Skattekontot stämmer inte med deklarationen'}
                            </div>
                            Deklarerat {Math.round(Number(status.expectedPayment || 0))} kr,
                            {' '}bokfört på skattekontot {Math.round(Number(status.actualPayment || 0))} kr
                            {Number(status.variance || 0) !== 0
                                && ` — skillnad ${Math.round(Number(status.variance))} kr`}.
                            {' '}Underlaget bygger på {status.matchedStatements} matchad(e) kontohändelse(r).
                            {reconciliation?.recommendation && (
                                <div className="small mt-1">{reconciliation.recommendation}</div>
                            )}
                        </div>
                    </Alert>
                )}
                {isCompleted ? (
                    <>
                        <div className="text-center py-4">
                            <CheckCircle2 size={48} className="text-success mb-3" />
                            <h4 className="mb-2" style={{ color: 'var(--primary-dark)' }}>Momsperioden är slutförd</h4>
                            <p className="text-muted">Alla steg har hanterats och bokförts.</p>
                        </div>

                        <div className="d-flex align-items-center justify-content-between">
                            {/* The verifikat themselves are chips in the header now,
                                reachable from every step — this block used to show
                                the dragning verifikat and nothing else, so a period
                                with a momsredovisning verifikat too showed only one
                                of the two. The undo stays here: it is specific to
                                this step and consequential enough not to sit in a
                                header chip. */}
                            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                Verifikat {recordVerification.series}{recordVerification.number} är bokfört.
                            </span>
                            {vatPeriod?.state === 'open' && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="text-danger p-0"
                                    onClick={() => handleDeleteVerification(recordVerification.verification_id, 'record')}
                                    disabled={operationInProgress || isPreviewMode}
                                    style={{ fontSize: '0.8rem' }}
                                >
                                    <Trash2 size={13} className="me-1" />
                                    Ångra
                                </Button>
                            )}
                        </div>

                        <VatVerificationViewModal
                            show={showVerificationModal}
                            onHide={() => setShowVerificationModal(false)}
                            verificationId={recordVerification.verification_id}
                        />
                    </>
                ) : (
                    <>
                        <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                            Skapa verifikat för när Skatteverket drar momsen från skattekontot ({accountLabel('vat_settlement', 'momsredovisningskontot')} → {accountLabel('tax_account', 'skattekontot')}).
                        </p>

                        <div className="d-flex align-items-end gap-3 mb-3">
                            <Form.Group style={{ width: '180px' }}>
                                <Form.Label className="small text-muted mb-1">Datum för dragning</Form.Label>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={recordDate}
                                    onChange={(e) => setRecordDate(e.target.value)}
                                    disabled={isPreviewMode}
                                />
                            </Form.Group>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleCreateRecordVerification(recordDate)}
                                disabled={operationInProgress || isPreviewMode || !recordDate}
                                className="d-inline-flex align-items-center"
                            >
                                <FileText size={16} className="me-2" />
                                Skapa verifikat
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RecordStep;
